// frontend-react/src/components/sales/CustomerAutocomplete.jsx
import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../api.js';

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function CustomerAutocomplete({
  buyer,
  setBuyer,
  phone,
  setPhone,
  selectedCustomer,
  setSelectedCustomer,
}) {
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestRows, setSuggestRows] = useState([]);
  const [suggestErr, setSuggestErr] = useState('');

  const debouncedBuyer = useDebounced(buyer, 250);
  const suggestRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    async function run() {
      const q = String(debouncedBuyer || '').trim();
      setSuggestErr('');

      if (selectedCustomer?.uid && q && q !== (selectedCustomer.name || '')) {
        setSelectedCustomer(null);
      }

      if (!q || q.length < 1) {
        setSuggestRows([]);
        setSuggestOpen(false);
        return;
      }

      setSuggestLoading(true);
      try {
        const r = await apiFetch(`/api/orders/customer-suggest?q=${encodeURIComponent(q)}&limit=10`);
        const rows = Array.isArray(r?.rows) ? r.rows : [];
        if (!mounted) return;

        setSuggestRows(rows);
        setSuggestOpen(true);
      } catch (e) {
        if (!mounted) return;
        setSuggestErr(e?.message || 'Erreur autocomplete');
        setSuggestRows([]);
        setSuggestOpen(true);
      } finally {
        if (mounted) setSuggestLoading(false);
      }
    }

    run();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedBuyer]);

  useEffect(() => {
    function onDocClick(e) {
      const el = suggestRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setSuggestOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function chooseCustomer(c) {
    setSelectedCustomer(c);
    setBuyer(c?.name || '');
    setPhone(c?.phone || '');
    setSuggestOpen(false);
  }

  function clearCustomer() {
    setSelectedCustomer(null);
    setBuyer('');
    setPhone('');
    setSuggestRows([]);
    setSuggestOpen(false);
  }

  return (
    <div className="relative" ref={suggestRef}>
      <input
        value={buyer}
        onChange={e => {
          setBuyer(e.target.value);
          setSuggestOpen(true);
        }}
        onFocus={() => {
          if (suggestRows.length) setSuggestOpen(true);
        }}
        className="border border-slate-200 px-3 py-2 rounded-lg w-full bg-white focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
        placeholder="Nom acheteur"
        autoComplete="off"
      />

      {selectedCustomer?.uid && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 inline-flex">
            Client existant
            <span className="ml-2 text-content-secondary">({selectedCustomer.uid.slice(0, 8)}...)</span>
          </div>
          <button
            type="button"
            onClick={clearCustomer}
            className="text-xs px-2 py-1 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Changer
          </button>
        </div>
      )}

      {suggestOpen && (suggestLoading || suggestErr || suggestRows.length > 0) && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          {suggestLoading && <div className="p-2 text-xs text-slate-500">Recherche...</div>}
          {suggestErr && <div className="p-2 text-xs text-red-600">{suggestErr}</div>}

          {!suggestLoading && !suggestErr && suggestRows.length === 0 && (
            <div className="p-2 text-xs text-slate-500">Aucun client</div>
          )}

          {!suggestLoading && !suggestErr && suggestRows.length > 0 && (
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {suggestRows.map(c => (
                <button
                  key={c.uid}
                  type="button"
                  onClick={() => chooseCustomer(c)}
                  className="w-full text-left p-2 hover:bg-slate-50"
                >
                  <div className="text-sm font-medium text-slate-900">{c.name || '—'}</div>
                  <div className="text-xs text-slate-500">
                    {c.phone || '—'}
                    {typeof c.total_orders !== 'undefined' ? ` - Cmd: ${c.total_orders}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
